import React, { useContext, useState } from "react";
import axios from "axios";
import { useAuthUser } from "./dashboard/hooks/useAuthUser";
import { formatPhoneNumber } from "./dashboard/utils/PhoneFormatters";
import { ModalContext } from "./dashboard/store/ModalContext";
import { useNavigate } from "react-router-dom";
import { callDeleteOwnAccount } from "../api/auth";

type newInfo = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: string;
  postalCode: string;
  city: string;
};

const UpdateProfileForm = () => {
  const {
    details,
    token,
    saveAccountProfile,
    isBusiness,
    hasCSASubscription,
    logout,
  } = useAuthUser();
  const { closeModal, openModal } = useContext(ModalContext);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<newInfo>({
    firstName: details?.firstName ?? "",
    lastName: details?.lastName ?? "",
    phoneNumber: details?.phoneNumber ?? "",
    address: details?.address ?? "",
    postalCode: details?.postalCode ?? "",
    city: details?.city ?? "",
  });

  const navigate = useNavigate();
  function handleChange(value: string, key: string) {
    const updatedProfileData = { ...profileData, [key]: value };
    setProfileData((prev) => {
      return updatedProfileData;
    });
  }

  async function handleUpdate() {
    if (!token) {
      alert("You are not logged in.");
      return;
    }

    if (isBusiness) {
      if (
        profileData.address.trim().length < 1 ||
        profileData.postalCode.trim().length < 1 ||
        profileData.city.trim().length < 1 ||
        !profileData.phoneNumber?.trim()
      ) {
        alert("Please fill out address, postal code, city, and phone number.");
        return;
      }
    } else {
      if (
        profileData.firstName.trim().length < 1 ||
        profileData.lastName.trim().length < 1 ||
        !profileData.phoneNumber?.trim()
      ) {
        alert(
          "Please fill in first name, last name, and a valid phone number.",
        );
        return;
      }
    }

    const phoneNumber = profileData.phoneNumber.trim();

    setSaving(true);
    try {
      if (isBusiness) {
        await saveAccountProfile({
          phoneNumber,
          address: profileData.address.trim(),
          postalCode: profileData.postalCode.trim(),
          city: profileData.city.trim(),
        });
      } else {
        await saveAccountProfile({
          firstName: profileData.firstName.trim(),
          surname: profileData.lastName.trim(),
          phoneNumber,
        });
      }
      closeModal();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const msg = e.response?.data?.message;
        alert(
          typeof msg === "string"
            ? msg
            : "Could not update profile. Try again.",
        );
      } else {
        alert("Could not update profile. Try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  const confirmDeleteAccount = async () => {
    if (!token) {
      alert("You are not logged in.");
      return;
    }

    try {
      await callDeleteOwnAccount(token);
      closeModal();
      logout();
      navigate("/");
    } catch (e) {
      const message = axios.isAxiosError(e) ? e.response?.data?.message : null;
      openModal({
        title: "Could not delete account",
        body:
          typeof message === "string"
            ? message
            : "Something went wrong. Please try again later.",
        showCloseButton: true,
      });
    }
  };

  const handleDeleteAccountClick = () => {
    if (hasCSASubscription) return;

    openModal({
      title: "Warning",
      body: (
        <p className="modal-exit-confirm-msg">
          This will permanently delete your account and cannot be undone.
        </p>
      ),
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes, delete",
          loadingText: "Deleting...",
          callback: confirmDeleteAccount,
        },
      ],
    });
  };

  return (
    <div className="edit-profile-container">
      {isBusiness ? (
        <div className="modal-input-single">
          <label>Company Name</label>
          <input value={details?.companyName} disabled />
        </div>
      ) : (
        <div className="modal-input-pair">
          <div className="modal-label-and-input-pair">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              value={profileData.firstName}
              onChange={(e) => handleChange(e.target.value, "firstName")}
              required
            />
          </div>
          <div className="modal-label-and-input-pair">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              value={profileData.lastName}
              onChange={(e) => handleChange(e.target.value, "lastName")}
              required
            />
          </div>
        </div>
      )}
      <div className="modal-input-pair">
        <div className="modal-label-and-input-pair">
          <label htmlFor="phoneNumber">Phone Number</label>
          <input
            type="text"
            value={formatPhoneNumber(profileData.phoneNumber)}
            onChange={(e) => handleChange(e.target.value, "phoneNumber")}
            required
          />
        </div>
        <div className="modal-label-and-input-pair">
          <label>Email</label>
          <input value={details?.email} disabled />
        </div>
      </div>
      {isBusiness && (
        <>
          <div className="modal-input-single">
            <div className="modal-label-and-input-pair">
              <label htmlFor="companyAddress">Company Address</label>
              <input
                type="text"
                value={profileData.address}
                onChange={(e) => handleChange(e.target.value, "address")}
                required
              />
            </div>
          </div>
          <div className="modal-input-pair">
            <div className="modal-label-and-input-pair">
              <label htmlFor="postalCode">Postal Code</label>
              <input
                type="text"
                value={profileData.postalCode}
                onChange={(e) => handleChange(e.target.value, "postalCode")}
                required
              />
            </div>
            <div className="modal-label-and-input-pair">
              <label htmlFor="city">City</label>
              <input
                type="text"
                value={profileData.city}
                onChange={(e) => handleChange(e.target.value, "city")}
                required
              />
            </div>
          </div>
        </>
      )}
      <button
        className="no-style-btn"
        onClick={() => {
          closeModal();
          navigate("/forgot-password");
        }}
      >
        I want to change my password!
      </button>
      <div className="edit-profile-delete-section">
        <button
          type="button"
          className={`btn delete-btn${hasCSASubscription ? " disabled" : ""}`}
          disabled={hasCSASubscription || saving}
          onClick={handleDeleteAccountClick}
        >
          Delete account
        </button>
        {hasCSASubscription && (
          <p className="edit-profile-delete-hint">
            Accounts with an active CSA subscription cannot be deleted. Please
            contact the farm if you need help.
          </p>
        )}
      </div>
      <div className="buttons-footer">
        <button className="btn equal-btns" onClick={closeModal}>
          Cancel
        </button>
        <button
          className="btn equal-btns"
          type="button"
          disabled={saving}
          onClick={() => handleUpdate()}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};

export default UpdateProfileForm;
